import { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdDescription, MdTopic } from "react-icons/md";
import { FaChevronDown } from "react-icons/fa";
import { useLoading } from "../common/LoadingContext";
import { useToast } from "../common/ToastContext";

import { terminateAssignment } from "../../services/assignmentService";

const TerminateAssignmentModal = ({
  assignment,
  onClose,
  onAssignmentUpdate,
}) => {
  const { showToast } = useToast();
  const [terminateDetails, setTerminateDetails] = useState("");
  const [selectedClosedReason, setSelectedClosedReason] = useState("");
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();

  const assignmentClosedReasons = {
    Completed: "completed",
    Cancelled: "cancelled",
    Failed: "failed",
    "Manually Terminated": "manually_terminated",
    "Past Deadline": "past_deadline",
  };

  const handleEscapeKey = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
        navigate("#");
      }
    },
    [onClose, navigate],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleEscapeKey]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "terminate_details") {
      setTerminateDetails(value);
    } else if (name === "closed_reason") {
      setSelectedClosedReason(assignmentClosedReasons[value] || value);
    }
  };

  const handleTerminateAssignment = async () => {
    if (!selectedClosedReason) {
      showToast("Please select a close reason", "error");
      return;
    }
    if (!terminateDetails) {
      showToast(
        "Please provide details for why termination is needed",
        "error",
      );
      return;
    }
    setIsLoading(true);
    try {
      const response = await terminateAssignment(
        assignment.id,
        selectedClosedReason,
        terminateDetails,
      );
      if (response.status === 200) {
        showToast("Assignment terminated successfully", "success");
        onAssignmentUpdate();
      }
      setIsLoading(false);
      onClose();
    } catch (error) {
      setIsLoading(false);
      console.error("Error terminating assignment:", error);
      showToast("Failed to terminate assignment", "error");
      return;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">Terminate Assignment</h3>
        <div className="mt-1 relative rounded-md shadow-sm">
          <MdTopic className="absolute left-2 top-3 text-gray-400" />
          <select
            name="closed_reason"
            value={selectedClosedReason || ""}
            onChange={handleChange}
            className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
          >
            <option value="">Select a close reason ...</option>
            {Object.entries(assignmentClosedReasons).map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <FaChevronDown className="absolute right-2 top-3 text-gray-400 pointer-events-none" />
        </div>
        <div className="mt-1 relative rounded-md shadow-sm">
          <MdDescription className="absolute left-2 top-3 text-gray-400" />
          <textarea
            name="terminate_details"
            value={terminateDetails || ""}
            onChange={handleChange}
            className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
            placeholder="Terminate details (required)"
            rows="4"
          />
        </div>
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => onClose()}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => handleTerminateAssignment()}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Terminate Assignment
          </button>
        </div>
      </div>
    </div>
  );
};

export default TerminateAssignmentModal;
